import { CommonModule, DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeUrl, Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterModule,
  RouterOutlet,
} from '@angular/router';
// ...existing code...
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthState } from '@okta/okta-auth-js';
import {
  Subscription,
  catchError,
  filter,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { buildNumber, version } from '../../package.json';
import {
  APPLICATION_NAME,
  ESQEP_AUTH_TYPE,
  IDLE,
  TIME_OUT,
} from './core/constants/constants.component';
import {
  HELP_CONTENT,
  HelpEntry,
} from './core/constants/help-file-url.component';
import { startClaimMenuCounts } from './feature/claims-v2/claims-v2.routes-providers';
import { DrawerAutosizeHackDirective } from './core/directives/DrawerAutosize.directive';
import { AlertData, AlertLevels } from './core/services/alert/alert.service';
import { AuthService } from './core/services/auth/auth.service';
import { AUTHENTICATION_CONFIGURATION_NAMES } from './core/services/auth/authenticationConfigNames';
import { BuildVersionsService } from './core/services/build-versions/build-versions.service';
import { ConfigService } from './core/services/config/config.service';
import { DialogService } from './core/services/dialog/dialog.service';
import { MenuService } from './core/services/menu/menu.service';
import { SelectedPersonService } from './core/services/selected-person/selected-person.service';
import { SpinnerService } from './core/services/spinner/spinner.service';
import { BrowserStorageService } from './core/utilities';
import { AlertComponent } from './ui/alert/alert.component';
import { PersonSelectorComponent } from './ui/person-selector/person-selector.component';
import { CustomSidenavComponent } from './ui/custom-sidenav/custom-sidenav.component';
import { FooterComponent } from './ui/footer/footer.component';
import { HeaderComponent } from './ui/header/header.component';
import { InactivityComponent } from './ui/inactivity/inactivity.component';

const idle = signal<number>(IDLE);
const timeout = signal<number>(TIME_OUT);

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterModule,
    AlertComponent,
    RouterOutlet,
    RouterModule,
    FooterComponent,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    MatToolbarModule,
    MatTooltipModule,
    HeaderComponent,
    CustomSidenavComponent,
    DrawerAutosizeHackDirective,
    MatDialogModule,
    NgIdleKeepaliveModule,
    PersonSelectorComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    {
      provide: MatDialogRef,
      useValue: {},
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.app-gui-build-version]': 'buildNumber()',
    '[attr.app-gui-version]': 'guiVersion()',
    '[attr.esqep-build-version]': 'esqepBuildVersionNumber()',
    '[attr.csod-esqep-build-version]': 'csodEsqepBuildVersionNumber()',
  },
})
export class AppComponent implements OnDestroy, OnInit, AfterViewInit {
  private readonly idle = inject(Idle);
  readonly helpEntry = signal<HelpEntry | undefined>(undefined);
  private loading: boolean = false;
  private logging: any;
  private authStatusSubs = new Subscription();
  private userGroupSubs = new Subscription();
  private appSubscriptions = new Subscription();
  private isIframe: boolean = false;
  private navigationSubscription: Subscription;
  private readonly destroyRef = inject(DestroyRef);
  private readonly homeMenu = inject(MenuService);
  private readonly storageService = inject(BrowserStorageService);
  private readonly configService = inject(ConfigService);
  private readonly spinnerService = inject(SpinnerService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly buildVersionsService = inject(BuildVersionsService);
  private readonly oktaStateService = inject(OktaAuthStateService, {
    optional: true,
  });
  private readonly selectedPersonService = inject(SelectedPersonService);
  private idleState: 'NOT_STARTED' | 'NOT_IDLE' | 'IDLE' | 'TIMED_OUT' =
    'NOT_STARTED';
  private oktaAuth = inject(OKTA_AUTH, { optional: true });
  private dialogRef = inject(MatDialogRef<InactivityComponent>);
  private domSanitizer = inject(DomSanitizer);
  private isCollapsed: string;
  /** Tracks whether the first (initial-load) navigation has completed, so we
   * don't steal focus into #main-content before the user can reach the skip link. */
  private hasNavigatedOnce = false;

  protected readonly guiVersion = signal(version);
  protected readonly buildNumber = signal(buildNumber);
  protected readonly title = signal<string>('');
  protected readonly helpUrl = signal<SafeUrl | undefined>(undefined);
  protected readonly isSignedIn = signal<boolean>(false);
  protected readonly appVersion = signal<string>('');
  protected readonly applicationName = signal<string>(APPLICATION_NAME);
  protected readonly dialog = inject(MatDialog);
  protected readonly dialogService = inject(DialogService);
  protected readonly authService = inject(AuthService);
  protected readonly givenName = signal<string>('');
  protected readonly upn = signal<string>('');
  protected readonly router = inject(Router);
  protected readonly collapsed = signal(false);
  protected readonly sidenavWidth = computed(() =>
    this.collapsed() ? '75px' : '250px',
  );
  private readonly zone = inject(NgZone);
  private readonly footerRef = viewChild('appFooter', { read: ElementRef });
  /** How much of the footer band is currently on screen. The sidenav is fixed to
   * the viewport, so without this its bottom edge is the bottom of the viewport
   * and the in-flow footer paints over the last menu items. Feeding this into
   * the drawer's `fixedBottomGap` stops it exactly where the footer starts,
   * while still using the full height whenever the footer is scrolled away. */
  protected readonly footerOverlap = signal(0);
  protected readonly isViewingOther = this.selectedPersonService.isViewingOther;
  protected readonly viewingPersonName =
    this.selectedPersonService.viewingPersonName;
  protected readonly viewingStartedAt =
    this.selectedPersonService.viewingStartedAt;

  protected readonly viewingPersonTooltip = computed(() => {
    const person = this.selectedPersonService.selectedPerson();
    if (!person) return '';
    const lines: string[] = [person.name];
    if (person.adUpn) lines.push(person.adUpn);
    if (person.location) lines.push(person.location.name);
    person.orgUnits?.forEach((ou) => lines.push(ou.name));
    return lines.join('\n');
  });

  protected onClearViewingAs(): void {
    this.selectedPersonService.clearSelection();
  }

  protected readonly buildVersions = toSignal(
    toObservable(this.isSignedIn).pipe(
      filter((signedIn) => signedIn),
      switchMap(() =>
        this.buildVersionsService.getBuildVersions().pipe(
          tap((buildVersions) => {
            if (!!buildVersions.error) {
              // console.error(
              //   `Error loading esqep build versions: ${buildVersions.error}`,
              // );
            }
          }),
          catchError((error) => {
            console.error('Error loading esqep build versions', error);
            return of(null);
          }),
        ),
      ),
    ),
  );

  protected readonly esqepBuildVersionNumber = computed(
    () => this.buildVersions()?.esqep || 'Not found',
  );

  protected readonly csodEsqepBuildVersionNumber = computed(
    () => this.buildVersions()?.csodEsqep || 'Not found',
  );

  exceptionData = {
    PERSON_UPN: {
      level: AlertLevels.ERROR,
      code: 'APC-001',
      message: 'Unable to retrieve user data',
    } as AlertData,
    USER_GROUP: {
      level: AlertLevels.ERROR,
      code: 'APC-003',
      message: 'Unable to retrieve user group info',
    } as AlertData,
    ADMIN_STATUS: {
      level: AlertLevels.ERROR,
      code: 'APC-004',
      message: 'Unable to retrieve user role info',
    } as AlertData,
    PERSON_UUID: {
      level: AlertLevels.ERROR,
      code: 'APC-005',
      message: 'Unable to retrieve user data',
    } as AlertData,
    SECURITY_ROLE: {
      level: AlertLevels.ERROR,
      code: 'APC-006',
      message: 'Unable to retrieve user security role',
    } as AlertData,
    PERSON_PHOTO: {
      level: AlertLevels.WARNING,
      code: 'APC-007',
      message: 'Unable to retrieve user photo',
    } as AlertData,
  };

  storeMenuState(collapsed: boolean) {
    this.storageService.set('isCollapsed', collapsed.toString());
  }

  private readonly document = inject(DOCUMENT);

  /** Keeps {@link footerOverlap} in step with where the footer actually sits.
   * The listeners run outside the zone — the signal only notifies when the
   * measurement changes, so scrolling doesn't drive change detection. */
  private trackFooterOverlap(): void {
    effect((onCleanup) => {
      const footer = this.footerRef()?.nativeElement as HTMLElement | undefined;
      const view = this.document.defaultView;
      if (!footer || !view) {
        this.footerOverlap.set(0);
        return;
      }

      const measure = () => {
        const distanceIntoViewport =
          view.innerHeight - footer.getBoundingClientRect().top;
        this.footerOverlap.set(Math.max(0, Math.round(distanceIntoViewport)));
      };

      // The footer moves when the page scrolls, when the viewport resizes and
      // when the routed content changes the document height, so watch all three.
      const observer = new ResizeObserver(measure);
      this.zone.runOutsideAngular(() => {
        measure();
        view.addEventListener('scroll', measure, { passive: true });
        view.addEventListener('resize', measure, { passive: true });
        observer.observe(footer);
        observer.observe(this.document.documentElement);
      });

      onCleanup(() => {
        view.removeEventListener('scroll', measure);
        view.removeEventListener('resize', measure);
        observer.disconnect();
      });
    });
  }

  constructor() {
    this.trackFooterOverlap();
    // Claims experience v2 sidenav counts; a no-op while the flag is off.
    startClaimMenuCounts();

    const preconnectLink = this.document.createElement('link');
    preconnectLink.rel = 'preconnect';
    preconnectLink.href = this.document.location.origin;
    this.document.head.appendChild(preconnectLink);

    this.idle.setIdle(this.idle_time ? this.idle_time : idle());
    this.idle.setTimeout(this.time_out ? this.time_out : timeout());
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.onIdleStart
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.idleState = 'IDLE';
        const config: MatDialogConfig<{}> = {
          width: '530px',
          height: '250px',
          hasBackdrop: true,
          disableClose: true,
          data: {
            title: 'Inactivity Warning',
            inactiveTime: this.time_out ? this.time_out : timeout(),
          },
        };
        this.dialogRef = this.dialogService.open(InactivityComponent, config);

        this.dialogRef
          .afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((response: unknown) => {
            if (response) {
              this.logout();
            } else {
              this.authService.authTimeoutSubj.next(true);
              this.reset();
            }
          });
      });

    this.idle.onIdleEnd
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.idleState = 'NOT_IDLE';
      });

    this.idle.onTimeoutWarning
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((secondsLeft: number) => {
        if (this.dialogRef && this.dialogRef.componentInstance) {
          this.dialogRef.componentInstance.updateData(secondsLeft);
        }
      });

    this.idle.onTimeout
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.idleState = 'TIMED_OUT';
        this.logout();
      });

    this.isCollapsed = this.storageService.get('isCollapsed');
    this.collapsed.set(this.isCollapsed === 'true' ? true : false);
  }

  private reset() {
    this.idle.watch();
    this.idleState = 'NOT_IDLE';
  }

  get idle_time(): number {
    return this.configService.idle;
  }

  get time_out(): number {
    return this.configService.time_out;
  }

  get spinnerInProgressUrls(): string {
    return this.spinnerService.inProgressUrls();
  }

  get inUserGroup(): boolean {
    return this.authService.inUserGroup;
  }

  get hasAdminRole(): boolean {
    return this.authService.hasAdminRole;
  }

  get clientLogoAsset(): string {
    return this.configService.clientLogoAsset;
  }

  get clientLogoAsset2(): string {
    return this.configService.clientLogoAsset2;
  }

  get clientHeading(): string {
    return this.configService.heading;
  }

  get clientSubTitle(): string {
    return this.configService.subTitle;
  }

  get navbarBgClass(): string {
    return this.configService.navbarBgClass;
  }

  clog = (msg: string, lvl: string | number) => {
    this.configService.logging.newMsg(msg, lvl);
  };

  ngAfterViewInit(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        // On the initial page load we deliberately leave focus where the browser
        // put it so the "Skip to main content" link is the first Tab stop (#5360).
        // On every in-app navigation after that we move focus to the skip link so it
        // is consistently the first Tab stop on every page, giving keyboard/screen-
        // reader users a reset focus point at the top of the new page (#5401).
        if (!this.hasNavigatedOnce) {
          this.hasNavigatedOnce = true;
          return;
        }
        const skipLink = document.getElementById('skip-link');
        if (skipLink) {
          // preventScroll: when focused after a mouse navigation the link stays
          // hidden (:focus-visible), so don't let focusing it shift the scroll.
          skipLink.focus({ preventScroll: true });
        }
      });
  }

  async ngOnInit() {
    // Restore authentication state on app startup
    const storedAuthType = this.storageService.get(ESQEP_AUTH_TYPE);
    this.reset();
    this.setupNavigationListener();
    this.authService.startSessionRevalidationOnRestore();

    sessionStorage.removeItem('msal.interaction.status');

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((url: NavigationEnd) => {
        if (url.url === '/home') {
          this.collapsed.set(false);
        } else {
          this.collapsed.set(true);
        }
      });

    this.isIframe = window !== window.parent && !window.opener;
    if (window !== window.parent && !window.opener) {
      this.clog('In iframe', 'INFO'); // For token refresh purposes, prevent multiple iframes from being created
    } else {
      this.spinnerService.reset();
      this.logging = this.configService.logging;
      this.appVersion.set(this.configService.appVersion);

      if (
        (this.configService.authSetup[
          AUTHENTICATION_CONFIGURATION_NAMES.ENTRA
        ] &&
          this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.ENTRA]
            .enabled) ||
        (this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.B2C] &&
          this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.B2C]
            .enabled)
      ) {
        // ENTRA/B2C: Use CustomMsalService for account logic
        const authType = this.storageService.get(ESQEP_AUTH_TYPE);
        if (
          authType === AUTHENTICATION_CONFIGURATION_NAMES.ENTRA ||
          authType === AUTHENTICATION_CONFIGURATION_NAMES.B2C
        ) {
          const msalInstance = this.authService.customMsalService.instance;
          if (msalInstance) {
            await msalInstance.initialize();
            const accounts = msalInstance.getAllAccounts();
            this.authService.isAuthenticated = accounts.length > 0;
            if (
              this.authService.isAuthenticated &&
              !msalInstance.getActiveAccount() &&
              accounts.length > 0
            ) {
              msalInstance.setActiveAccount(accounts[0]);
            }
          }
        }
        this.router.events
          .pipe(filter((event) => event instanceof NavigationEnd))
          .subscribe((url: NavigationEnd) => {
            if (url.url === '/') {
              this.router.navigate(['/home']);
            }
            window.scrollTo(0, 0);
          });
      }

      if (
        this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.OKTA]
          ?.enabled &&
        this.storageService.get(ESQEP_AUTH_TYPE) ===
          AUTHENTICATION_CONFIGURATION_NAMES.OKTA
      ) {
        this.oktaStateService.authState$
          .pipe(
            filter((s: AuthState) => !!s),
            map((s: AuthState) => s.isAuthenticated ?? false),
          )
          .subscribe((s) => {
            if (s) {
              this.authService.isAuthenticated = true;
              this.givenName.set(this.authService.me?.givenName);
              this.upn.set(this.authService.userUPN);
            }
          });
      }
      this.authStatusSubs = this.authService.authenticationStatusSubj.subscribe(
        (a) => {
          if (
            (this.configService.authSetup[
              AUTHENTICATION_CONFIGURATION_NAMES.ENTRA
            ] &&
              this.configService.authSetup[
                AUTHENTICATION_CONFIGURATION_NAMES.ENTRA
              ].enabled) ||
            (this.configService.authSetup[
              AUTHENTICATION_CONFIGURATION_NAMES.B2C
            ] &&
              this.configService.authSetup[
                AUTHENTICATION_CONFIGURATION_NAMES.B2C
              ].enabled)
          ) {
            this.isSignedIn.set(
              a ||
                this.authService.customMsalService.instance?.getAllAccounts()
                  .length > 0,
            );
          } else {
            this.isSignedIn.set(a);
          }
          if (this.authService.isAuthenticated) {
            this.authService.getLoggedInUser().subscribe({
              next: (u) => {
                this.authService.userInfo = u;
                this.authService.storeUserPersonDetail(u);
                this.appSubscriptions.add(
                  this.authService.populateSecurityRoles(),
                );
                this.givenName.set(this.authService.me?.givenName);
                this.upn.set(this.authService.userUPN);
              },
              error: () => {
                console.error('Unable to retrieve logged in user');
                // logged-in user could not be retrieved; app continues unauthenticated
              },
            });
          }
        },
      );
    }
  }

  ngOnDestroy() {
    this.authStatusSubs.unsubscribe();
    this.appSubscriptions.unsubscribe();
    this.navigationSubscription.unsubscribe();
  }

  onRouterOutletActivate(event: any) {
    this.configService.componentName = event.constructor.name;
  }

  /**
   * Moves focus to the current page's main content. We handle this in code rather
   * than letting the browser/router follow the "#main-content" fragment, because a
   * fragment navigation re-runs routing (onSameUrlNavigation: 'reload') and could
   * bounce the user to the default route instead of staying on the current page (#5360).
   */
  skipToMainContent(event: Event): void {
    event.preventDefault();
    const main = document.getElementById('main-content');
    if (main) {
      main.focus();
      main.scrollIntoView();
    }
  }

  logout() {
    this.authService.logout();
  }

  getPageRouteData(): {
    title?: string;
    itemTranslationKey?: string;
    helpEntry?: HelpEntry;
  } {
    let child = this.activatedRoute.firstChild;
    if (!child) {
      return this.activatedRoute.snapshot.data;
    }
    while (child.firstChild) {
      child = child.firstChild;
    }
    if (child.snapshot.data.title) {
      const data = child.snapshot.data;
      const resolver = data['titleByParam'] as
        | { param: string; map: Record<string, string> }
        | undefined;
      const resolved = resolver?.map[child.snapshot.params[resolver.param]];
      return resolved ? { ...data, title: resolved } : data;
    }
    return null;
  }

  private setupNavigationListener() {
    this.navigationSubscription = this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError,
        ),
        tap((event) => (this.loading = event instanceof NavigationStart)),
        filter((event) => event instanceof NavigationEnd),
        map(() => this.getPageRouteData()),
        switchMap((routeData) => {
          // removed whislt resetting the translation stuff
          // if (routeData?.itemTranslationKey) {
          //   return this.translocoService
          //     .selectTranslate(routeData.itemTranslationKey)
          //     .pipe(map((title) => ({ ...routeData, title })));
          // } else {
          return of({ ...routeData });
          // }
        }),
        startWith(null),
      )
      .subscribe((data) => {
        const pageTitle = data?.title ?? null;
        this.title.set(pageTitle);
        this.titleService.setTitle(
          pageTitle ? `${pageTitle} - ${APPLICATION_NAME}` : APPLICATION_NAME,
        );
        this.helpEntry.set(data?.helpEntry ?? HELP_CONTENT.Default);
      });
  }
}
